import { BOX_SHADOW_8_14_26 } from '@apps/card/constants/boxShadow';
import type { TemplateForAutofill } from '@apps/card/contexts/AutofillContext';
import { useAutofill } from '@apps/card/contexts/AutofillContext';
import { useCardPreviewWithPhotoContext } from '@apps/card/contexts/CardPreviewWithPhotoContext';
import { useScaleToContainer } from '@apps/card/hooks/useScaleToContainer';
import { CardDieCut } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { Page } from '@apps/card/routes/CardCustomizer/components/Page';
import { cardDieCutToShapeConfig } from '@apps/card/utils';
import { buildCssVarStyle, imageVarIndices } from '@apps/card/utils/colorway';
import { autofillPhotos } from '@apps/card/utils/injectPhotosIntoTemplate';
import { animationTransition } from '@shared/utils/animationTransition';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { Box, Flex } from '@withjoy/joykit';
import { cloneDeep } from 'lodash-es';
import type { CSSProperties, ReactNode } from 'react';
import React from 'react';

type CardThumbnailProps = {
  children?: ReactNode;
  template: TemplateForAutofill;
  /**
   * Which colorway to preview. Omit to show the template's persisted colorway
   * (or the first variant if none is persisted); pass a value to reflect an
   * interactive selection (e.g. gallery swatches).
   */
  selectedColorwayId?: string;
  /**
   * Static preview image shown for designs that have no `themeJSON` to render live
   * (digital-only / legacy ecards). Opt-in from the caller so this stays scoped to
   * digital-only designs — a paperAndDigital template also carries a
   * `digitalDesignThumbnailUrl`, and inferring the fallback from that would render
   * the ecard image for a merely malformed paper template.
   */
  fallbackImageUrl?: string;
};

// Shared frame for both thumbnail variants (live <Page> and static fallback image)
// so the aspect ratio / padding / hover background can't drift between them.
const ThumbnailFrame = React.forwardRef<HTMLDivElement, { children: ReactNode; isMobile: boolean; style?: CSSProperties }>(({ children, isMobile, style }, ref) => (
  <Flex
    ref={ref}
    aspectRatio={isMobile ? '8 / 10' : '9 / 10'}
    overflow="hidden" // Needed for Safari
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    position="relative" // For badges
    paddingX={isMobile ? 4 : 8}
    paddingY={8}
    background="hsla(45, 12%, 93%, 1)"
    transition={animationTransition('background')}
    _groupHover={{
      background: 'hsla(45, 12%, 73%, 0.4)',
    }}
    // `contain: size` makes this frame size purely from its aspect-ratio and the grid track —
    // never from its contents. `useScaleToContainer` writes an explicit pixel size onto the
    // decorator below; without containment that size propagates up through this frame's
    // aspect-ratio and the tile's, back into the grid track, and the measure→write→relayout
    // loop latches into a wrong state under fast Safari zoom (the misalignment that only a
    // reload clears). Containment severs that loop so the measured frame stays stable.
    style={{ contain: 'size', ...style }}
  >
    {children}
  </Flex>
));

export const CardThumbnail = (props: CardThumbnailProps) => {
  const { children, template, selectedColorwayId, fallbackImageUrl } = props;
  const isMobile = useIsMobileScreen();

  const [containerRef, decoratorRef, contentRef] = useScaleToContainer<HTMLDivElement>(40);

  const { autofill, autofillPhotos: autofillPhotosFromContext } = useAutofill();
  const { photos: previewPhotos, isEnabled: isPreviewEnabled } = useCardPreviewWithPhotoContext();

  // Skip templates with missing or malformed themeJSON early — before autofill and
  // photo-injection which also dereference themeJSON.card. Digital-only designs
  // (legacy free ecards) have no themeJSON but pass a `fallbackImageUrl`, so render
  // their static preview instead of nothing.
  if (!template.themeJSON?.card?.front) {
    if (!fallbackImageUrl) return null;
    return (
      <ThumbnailFrame isMobile={isMobile}>
        <Box
          height="100%"
          boxShadow={BOX_SHADOW_8_14_26}
          style={{ aspectRatio: '5 / 7' }}
        >
          <img
            src={fallbackImageUrl}
            alt=""
            loading="lazy"
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </Box>
        {children}
      </ThumbnailFrame>
    );
  }

  // Resolve the colorway into inline CSS vars for preview. Derived per render
  // (cheap, pure) so the correct colorway shows as soon as the template loads —
  // no stale-state trap from initializing before async data arrives.
  const front = template.themeJSON.card.front;
  const options = front.colorways?.options;
  const colorwayId = selectedColorwayId ?? front.colorways?.selectedId ?? options?.[0]?.id;
  const selectedColorway = options?.find(o => o.id === colorwayId)?.values;
  const cssVarStyle = selectedColorway ? buildCssVarStyle(selectedColorway, imageVarIndices(front.layers ?? []), 'small') : {};
  const defaultCut = front.dimensions?.defaultCut ?? CardDieCut.rectangle;

  const clonedTemplate = cloneDeep(template);
  autofill(clonedTemplate);

  if (previewPhotos.length > 0 && !template.themeId?.startsWith('upload_your_')) {
    // User selected preview photos — inject them into image slots so the gallery shows
    // templates filled with their own photos. Skip upload_your_ templates as they have
    // their own dedicated upload affordance and should remain blank until the user uploads.
    autofillPhotos(
      clonedTemplate,
      previewPhotos.map(p => ({ url: p.url })),
    );
  } else if (!isPreviewEnabled) {
    // Feature is off — auto-populate from the event album as usual.
    autofillPhotosFromContext(clonedTemplate);
  }
  // Feature on but no photos selected yet: leave image slots blank.

  return (
    <ThumbnailFrame
      ref={containerRef}
      isMobile={isMobile}
      style={cssVarStyle}
    >
      <Box
        ref={decoratorRef}
        boxShadow={BOX_SHADOW_8_14_26}
        borderRadius={cardDieCutToShapeConfig(defaultCut).borderRadius}
        transition="border-radius .3s"
        // Absolutely positioned + centered so the explicit width/height `useScaleToContainer`
        // writes here stay out of flow and can't resize the frame (see the `contain: size`
        // note above — together they break the Safari fast-zoom feedback loop). The frame is
        // `position: relative`, so this centers within it exactly as the previous flex centering did.
        position="absolute"
        top="50%"
        left="50%"
        style={{ transform: 'translate(-50%, -50%)' }}
      >
        <Page
          ref={contentRef}
          page={clonedTemplate.themeJSON!.card.front}
          width={5}
          height={7}
          rendition="small"
          shape={defaultCut}
          forcePlaceholderQRCode
        />
      </Box>
      {children}
    </ThumbnailFrame>
  );
};
